import React from 'react';
import { useIntl } from 'react-intl';
import moment from 'moment';
import { Box, Grid, Typography, Card } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import LaborHours from './LaborHours';
import { InformationAlert } from '../../Common';
import { testResultsOptions } from '../utils';
import { TranslateW } from '../../../Utils/Languages/Translate';
import { trakrefFontFamily } from '../../../utils';
import {
    trakrefBlue,
    Grey1,
    Grey3,
    Grey6,
    red,
} from '../../../color_utils';

// ---------------------------------------------------------------------------
// BASE UI for the Completion review page (ADO #14716).
// Renders one read-only summary card per visited workflow step in a two-column
// grid, with INCOMPLETE badges + "Action Required" indicators driven by the
// live required-field list. Labor Hours / GL Code stay editable at the bottom.
//
// NOTE: this is the base scaffold. The detailed field mapping for every SE
// type, the Gas Transaction Recovery/Install sub-section tables, and the
// "Submit Work Order" disable wiring are intentionally left as follow-ups.
// Add/refine per-step fields in CARD_CONFIG below.
// ---------------------------------------------------------------------------

// Options are shaped { id, name }. Resolve an id to its display label.
const labelFor = (options = [], id) => {
    if (id === null || id === undefined || id === '') return '';
    const match = (options || []).find(o => o.id === id || o.value === id);
    return match ? (match.name ?? match.label ?? '') : '';
};

// Leak locations display as the combined "Category — Location" value (ADO #15518),
// matching Recent Leaks and the Leak Location autocomplete. The category comes from
// the option's `category` id, resolved through leakLocationCategories.
const leakLocationLabelFor = (opts, id) => {
    if (id === null || id === undefined || id === '') return '';
    const location = (opts.leakLocations || []).find(o => o.id === id);
    if (!location) return '';
    const category = (opts.leakLocationCategories || []).find(c => c.id === location.category);
    return category ? `${category.name} — ${location.name}` : location.name;
};

const yesNo = v => (v === true ? 'Yes' : v === false ? 'No' : '');

const formatDate = d => (d && moment(d).isValid() ? moment(d).format('MM/DD/YYYY') : '');

const isEmptyValue = v => v === '' || v === null || v === undefined;

// Humanize a bare formState key (e.g. "leakLocationId" -> "Leak Location") for any
// required field we haven't explicitly mapped in CARD_CONFIG, so it still shows up.
const humanizeKey = key => key
    .replace(/Id$/, '')
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, c => c.toUpperCase())
    .trim();

// Per-step card configuration. `requiredKey` matches the bare field name used in
// serviceEventTypesRequiredFields / requiredFieldsRef so we can flag Action Required.
// `labelKey` is the i18n key the step's own field uses, so the review label matches the
// field label exactly (and stays localized); `label` is the fallback if a key is missing.
const CARD_CONFIG = {
    RepairDetails: {
        title: 'Discovery',
        // validateRepairDetails reports missing fields nested under this key.
        nestedKey: 'initialLeakInspection',
        fields: [
            { label: 'Leak Detection Method', labelKey: 'leakInspectionView.leak-detection-method', requiredKey: 'leakDetectionMethodId', get: (fs, opts) => labelFor(opts.leakDetectionMethods, fs.initialLeakInspection?.leakDetectionMethodId) },
            { label: 'Leak Found', labelKey: 'leakOrCircuit', requiredKey: 'wasLeakFound', get: fs => yesNo(fs.initialLeakInspection?.wasLeakFound) },
            { label: 'Date Leak Found', labelKey: 'leakLocationAndCause.date-leak-found', requiredKey: 'inspectionDate', visible: fs => fs.initialLeakInspection?.wasLeakFound === true, get: fs => formatDate(fs.initialLeakInspection?.inspectionDate) },
            { label: 'Leak Location', labelKey: 'leakLocationAndCause.leak-location', requiredKey: 'leakLocationId', visible: fs => fs.initialLeakInspection?.wasLeakFound === true, get: (fs, opts) => leakLocationLabelFor(opts, fs.initialLeakInspection?.leakLocationId) },
            { label: 'Cause of Leak', labelKey: 'leakLocationAndCause.cause-of-leak-for-asset', requiredKey: 'faultCauseTypeId', visible: fs => fs.initialLeakInspection?.wasLeakFound === true, get: (fs, opts) => labelFor(opts.leakCauses, fs.initialLeakInspection?.faultCauseTypeId) },
            { label: 'Estimated Leak Amount', labelKey: 'EstimatedLeakAmount', visible: fs => fs.initialLeakInspection?.wasLeakFound === true, get: fs => fs.initialLeakInspection?.estimatedLeakAmount },
            { label: 'Notes', labelKey: 'retrofitView.notes', get: fs => fs.initialLeakInspection?.notes },
        ],
    },
    RepairNotes: {
        title: 'Repair',
        fields: [
            // Repair Status (Repair Outcome) only renders in the Repair step when a leak was found
            // (RepairNotes.js showRepairOutcome), so mirror that here.
            { label: 'Repair Status', labelKey: 'repairOutcome.label', requiredKey: 'repairOutcomeTypeId', visible: fs => fs.initialLeakInspection?.wasLeakFound === true, get: (fs, opts) => labelFor(opts.repairOutcomeTypes, fs.repairOutcomeTypeId) },
            { label: 'Service Action', labelKey: 'generalService.service-action', requiredKey: 'serviceActionId', get: (fs, opts) => labelFor(opts.serviceActions, fs.serviceActionId) },
            { label: 'Repair Notes', labelKey: 'leakRepair.repair-notes', requiredKey: 'repairNotes', get: fs => fs.repairNotes },
            { label: 'Parts Required', labelKey: 'generalService.parts-required', get: fs => fs.partsRequired },
        ],
    },
    InitialVerificationTest: {
        title: 'Verification',
        nestedKey: 'initialVerificationLeakInspection',
        fields: [
            { label: 'Verification Date', labelKey: 'repairDate', requiredKey: 'inspectionDate', visible: fs => fs.initialVerificationLeakInspection?.wasLeakFound === false, get: fs => formatDate(fs.initialVerificationLeakInspection?.inspectionDate) },
            { label: 'Leak Detection Method', labelKey: 'leakInspectionView.leak-detection-method', requiredKey: 'leakDetectionMethodId', get: (fs, opts) => labelFor(opts.leakDetectionMethods, fs.initialVerificationLeakInspection?.leakDetectionMethodId) },
            // Verification Results stores Pass/Fail inverted in wasLeakFound (Pass = no leak found),
            // so resolve through testResultsOptions like the step's toggle — not yesNo (#15425).
            { label: 'Verification Results', labelKey: 'leakRepair.verification-results', requiredKey: 'wasLeakFound', get: fs => labelFor(testResultsOptions, fs.initialVerificationLeakInspection?.wasLeakFound) },
            // "Was Gas Transferred?" toggle lives on the IVT step (SLR, #14714); default Yes mirrors the step.
            { label: 'Was Gas Transferred?', get: fs => yesNo(fs.wasGasTransferred ?? true) },
        ],
    },
    GasTransaction: {
        title: 'Gas Transaction',
        // TODO: replace with Recovery/Install sub-section tables (TransferTable).
        // AC15 (loosened, #15492): flag INCOMPLETE only when the "Was Gas Transferred?" toggle is
        // reachable — i.e. a leak/circuit was found at Discovery so the Verification step that hosts
        // the toggle is rendered — and it's Yes with no Recover/Install entry. When Leak Found = No
        // the toggle isn't rendered (defaults Yes), so the card must not be flagged.
        incompleteWhen: fs => fs.initialLeakInspection?.wasLeakFound === true
            && (fs.wasGasTransferred ?? true) === true
            && !(fs.recoveries?.length > 0)
            && !(fs.installs?.length > 0),
        fields: [
            { label: 'Recovery Entries', get: fs => (fs.recoveries?.length ? String(fs.recoveries.length) : '') },
            { label: 'Install Entries', get: fs => (fs.installs?.length ? String(fs.installs.length) : '') },
        ],
    },
    FollowUpVerificationService: {
        title: 'Follow Up Verification',
        nestedKey: 'verificationLeakInspection',
        fields: [
            { label: 'Follow Up Date', labelKey: 'leakRepair.date-followup-service', requiredKey: 'inspectionDate', get: fs => formatDate(fs.verificationLeakInspection?.inspectionDate) },
            // Leak Repair Status (#15368) — mirrors the FVT step's field (FollowUpVerificationTest.js),
            // which renders only when a leak was found at Discovery. Maps to the
            // leakRepairDispositionTypeId backend field, resolved via the repairStatuses options.
            // Mapping it here (vs. the humanized fallback) shows the entered value AND replaces the
            // raw "Leak Repair Disposition Type" label with the field's real label.
            { label: 'Leak Repair Status', labelKey: 'showLeakRepairStatus.leak-repair-status', requiredKey: 'leakRepairDispositionTypeId', visible: fs => fs.initialLeakInspection?.wasLeakFound === true, get: (fs, opts) => labelFor(opts.repairStatuses, fs.verificationLeakInspection?.leakRepairDispositionTypeId) },
            { label: 'Leak Detection Method', labelKey: 'leakInspectionView.leak-detection-method', requiredKey: 'leakDetectionMethodId', get: (fs, opts) => labelFor(opts.leakDetectionMethods, fs.verificationLeakInspection?.leakDetectionMethodId) },
            { label: 'Verification Results', labelKey: 'leakRepair.verification-results', requiredKey: 'wasLeakFound', get: fs => labelFor(testResultsOptions, fs.verificationLeakInspection?.wasLeakFound) },
            { label: 'Leak Location', labelKey: 'leakLocationAndCause.leak-location', requiredKey: 'leakLocationId', visible: fs => fs.verificationLeakInspection?.wasLeakFound === true, get: (fs, opts) => leakLocationLabelFor(opts, fs.verificationLeakInspection?.leakLocationId) },
            { label: 'Cause of Leak', labelKey: 'leakLocationAndCause.cause-of-leak-for-asset', requiredKey: 'faultCauseTypeId', visible: fs => fs.verificationLeakInspection?.wasLeakFound === true, get: (fs, opts) => labelFor(opts.leakCauses, fs.verificationLeakInspection?.faultCauseTypeId) },
            { label: 'Estimated Leak Amount', labelKey: 'EstimatedLeakAmount', visible: fs => fs.verificationLeakInspection?.wasLeakFound === true, get: fs => fs.verificationLeakInspection?.estimatedLeakAmount },
            { label: 'Status After Service', labelKey: 'generalService.status-after-service', get: (fs, opts) => labelFor(opts.afterServiceStatusOptions, fs.finalCoolingApplianceStatusId) },
            { label: 'Notes', labelKey: 'Notes', get: fs => fs.verificationLeakInspection?.notes },
        ],
    },
};

const ActionRequiredValue = ({ onClick }) => (
    <Typography
        onClick={onClick}
        sx={{
            color: trakrefBlue,
            fontFamily: trakrefFontFamily,
            fontSize: '13px',
            fontWeight: 600,
            textDecoration: 'underline',
            cursor: onClick ? 'pointer' : 'default',
        }}
    >
        Action Required
    </Typography>
);

// Required keys that are satisfied implicitly by another (mapped) field and so
// must NOT render their own row. leakLocationCategoryId is consolidated into the
// single Leak Location autocomplete (#14542), so it has no standalone UI field.
const SUPPRESSED_REQUIRED_KEYS = new Set(['leakLocationCategoryId']);

// validateAllSteps() returns a MIXED-shape missingFieldsObject: some steps report
// flat keys (e.g. RepairNotes -> { serviceActionId: true }) while others nest under
// a sub-object (e.g. RepairDetails -> { initialLeakInspection: { wasLeakFound: true } },
// IVT -> { initialVerificationLeakInspection: {...} }). Check the card's nested bucket
// first (so Discovery/Verification/Follow-Up flag correctly and don't collide on the
// shared bare keys), then fall back to a flat top-level key.
const isRequiredMissing = (missingFieldsObject, nestedKey, key) => {
    if (nestedKey) {
        const nested = missingFieldsObject[nestedKey];
        if (nested && typeof nested === 'object' && nested[key]) return true;
    }
    return Boolean(missingFieldsObject[key]);
};

// Evaluate a card's rows + completeness. "Action Required" is driven by the app's
// own validation (missingFieldsObject from validateAllSteps) so it respects
// conditional requirements (e.g. IVT not required when Repair Status = "No Repair
// Attempted"). The per-card value getter disambiguates fields that share a bare
// key across steps (e.g. leakDetectionMethodId on Discovery vs Verification).
const evaluateCard = (config, requiredKeys, formState, dropdownOptions, missingFieldsObject) => {
    const configuredRequiredKeys = new Set(config.fields.filter(f => f.requiredKey).map(f => f.requiredKey));

    // Show a row for any required field we haven't mapped — but only when the app's
    // validation actually reports it missing, so satisfied/consolidated keys stay hidden.
    const fallbackFields = requiredKeys
        .filter(key => !configuredRequiredKeys.has(key)
            && !SUPPRESSED_REQUIRED_KEYS.has(key)
            && isRequiredMissing(missingFieldsObject, config.nestedKey, key))
        .map(key => ({ label: humanizeKey(key), requiredKey: key, get: () => '' }));

    // Only show fields that are actually visible in their wizard step. A field may declare a
    // `visible(formState)` predicate mirroring the step's conditional rendering (e.g. leak
    // location/cause only appear when a leak was found); fields with no predicate always show.
    const rows = [...config.fields, ...fallbackFields]
        .filter(field => !field.visible || field.visible(formState))
        .map(field => {
        const value = field.get ? field.get(formState, dropdownOptions) : '';
        const actionRequired = Boolean(field.requiredKey)
            && !SUPPRESSED_REQUIRED_KEYS.has(field.requiredKey)
            && isRequiredMissing(missingFieldsObject, config.nestedKey, field.requiredKey)
            && isEmptyValue(value);
        return { ...field, value, actionRequired };
    });

    // A card may also declare a card-level `incompleteWhen(formState)` rule for completeness that
    // isn't a single required field (e.g. Gas Transaction needs ≥1 recovery/install entry).
    const cardIncomplete = typeof config.incompleteWhen === 'function' && config.incompleteWhen(formState);

    return { rows, incomplete: rows.some(r => r.actionRequired) || cardIncomplete };
};

const StepCard = ({ config, stepId, formState, dropdownOptions, requiredKeys, missingFieldsObject, onCardClick }) => {
    const intl = useIntl();
    const { rows, incomplete } = evaluateCard(config, requiredKeys, formState, dropdownOptions, missingFieldsObject);
    // Card header uses the step's own wizard/stepper label so the title always matches the step
    // name exactly (e.g. RepairNotes -> "Repair"), and respects locale.
    const title = intl.formatMessage({ id: stepId }) || config.title;
    // Resolve each row's label from the same i18n key the step's field uses, so the review
    // labels match the field labels exactly; fall back to the literal `label` when no key.
    const labelText = field => (field.labelKey ? intl.formatMessage({ id: field.labelKey }) : field.label);

    const handleCardClick = onCardClick ? () => onCardClick(stepId) : undefined;

    return (
        <Card
            variant="outlined"
            sx={{ height: '100%' }}
        >
            <Box
                sx={{
                    alignItems: 'center',
                    backgroundColor: Grey6,
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '10px 16px',
                }}
            >
                <Typography
                    className="completion-card-title"
                    sx={{
                        color: Grey1,
                        fontFamily: trakrefFontFamily,
                        fontSize: '12px',
                        fontWeight: 700,
                        letterSpacing: '0.5px',
                        textTransform: 'uppercase',
                          cursor: handleCardClick ? 'pointer' : 'default',
                    ...(handleCardClick && { '&:hover .completion-card-title': { color: trakrefBlue, textDecoration: 'underline' } }),
                    }}
                    
                    onClick={handleCardClick}
                >
                    {title}
                </Typography>
                {incomplete && (
                    <Box sx={{ alignItems: 'center', color: red, display: 'flex', gap: '4px' }}>
                        <WarningAmberIcon sx={{ fontSize: 16 }} />
                        <Typography sx={{ color: red, fontFamily: trakrefFontFamily, fontSize: '12px', fontWeight: 700 }}>
                            INCOMPLETE
                        </Typography>
                    </Box>
                )}
            </Box>
            <Box>
                {rows.map(field => (
                    <Box
                        key={field.labelKey || field.label}
                        sx={{
                            alignItems: 'center',
                            display: 'flex',
                            justifyContent: 'space-between',
                            gap: '12px',
                            padding: '10px 16px',
                        }}
                    >
                        <Typography
                            sx={{
                                color: field.actionRequired ? red : Grey3,
                                fontFamily: trakrefFontFamily,
                                fontSize: '13px',
                                fontWeight: field.actionRequired ? 600 : 400,
                            }}
                        >
                            {labelText(field)}
                        </Typography>
                        {field.actionRequired ? (
                            <ActionRequiredValue onClick={handleCardClick} />
                        ) : (
                            <Typography
                                sx={{
                                    color: Grey1,
                                    fontFamily: trakrefFontFamily,
                                    fontSize: '13px',
                                    textAlign: 'right',
                                }}
                            >
                                {isEmptyValue(field.value) ? '—' : field.value}
                            </Typography>
                        )}
                    </Box>
                ))}
            </Box>
        </Card>
    );
};

const Completion = ({
    formState,
    setGlobalState,
    dropdownOptions,
    isEditing,
    isAdmin,
    serviceEventSteps = [],
    skippedStepIds = [],
    requiredFieldsRef,
    missingFieldsObject = {},
    onCardClick,
}) => {
    // Cards for every visited step except Enter Service (shown in the header) and
    // Completion itself. Skipped (conditionally absent) steps are excluded.
    const cardSteps = serviceEventSteps
        .map(s => s.props.id)
        .filter(id => id !== 'EnterService' && id !== 'Completion' && !skippedStepIds.includes(id));

    const requiredFor = stepId => (requiredFieldsRef?.current?.[stepId]) || [];

    const cards = cardSteps.map(stepId => {
        const config = CARD_CONFIG[stepId] || { title: stepId, fields: [] };
        return { stepId, config };
    });

    // Banner shows when any card has a genuinely-missing required field (per the app's
    // own validation), so it clears once everything required is filled.
    const anyIncomplete = cards.some(({ stepId, config }) =>
        evaluateCard(config, requiredFor(stepId), formState, dropdownOptions, missingFieldsObject).incomplete
    );

    // Enter Service is intentionally not a review card (its read-only details live in the header),
    // and it's sliced out of the stepper, so a missing required Enter Service field (e.g. Notes on
    // Install) previously disabled Complete with no on-screen indicator. Surface those by name.
    const enterServiceFieldLabels = {
        repairNotes: 'Notes',
        serviceActionId: 'Service Action',
        primaryTechnicianId: 'Primary Technician',
        serviceDate: 'Service Date',
        assetId: 'Equipment Worked On',
        newLocationId: 'New Location',
    };
    const enterServiceMissing = (requiredFieldsRef?.current?.EnterService || [])
        .filter(key => missingFieldsObject[key])
        .map(key => enterServiceFieldLabels[key] || humanizeKey(key));

    return (
        <Box>
            <Box sx={{ marginBottom: 2 }}>
                <Typography sx={{ color: Grey1, fontFamily: trakrefFontFamily, fontSize: '16px', fontWeight: 600 }}>
                    {TranslateW('Completion') || 'Completion'}
                </Typography>
                <Typography sx={{ color: Grey3, fontFamily: trakrefFontFamily, fontSize: '13px' }}>
                    Review all entered data before submitting the service event.
                </Typography>
            </Box>

            {enterServiceMissing.length > 0 && (
                <Box sx={{ marginBottom: 2 }}>
                    <InformationAlert
                        severity="error"
                        title="Enter Service Incomplete"
                        message={`Complete these required Enter Service field(s) before submitting: ${enterServiceMissing.join(', ')}. Return to the Enter Service step to edit them.`}
                        onClose={null}
                    />
                </Box>
            )}

            {anyIncomplete && (
                <Box sx={{ marginBottom: 2 }}>
                    <InformationAlert
                        severity="error"
                        title="Action Required"
                        message={"Please review the cards below and complete all fields marked 'Action Required.'"}
                        onClose={null}
                    />
                </Box>
            )}

            <Grid container spacing={2}>
                {cards.map(({ stepId, config }) => (
                    <Grid item xs={12} md={6} key={stepId}>
                        <StepCard
                            config={config}
                            stepId={stepId}
                            formState={formState}
                            dropdownOptions={dropdownOptions}
                            requiredKeys={requiredFor(stepId)}
                            missingFieldsObject={missingFieldsObject}
                            onCardClick={onCardClick}
                        />
                    </Grid>
                ))}
            </Grid>

            <Box sx={{ marginTop: 3, paddingTop: 3 }}>
                <LaborHours
                    formState={formState}
                    setGlobalState={setGlobalState}
                    dropdownOptions={dropdownOptions}
                    isEditing={isEditing}
                    isAdmin={isAdmin}
                />
            </Box>
        </Box>
    );
};

export default Completion;
